import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Switch } from "@headlessui/react";
import { toast } from "sonner";
import {
  btnPrimaryCls,
  btnSecondaryCls,
  inputCls,
  labelCls,
} from "components/tw/classes";
import { vilniusYmd } from "utils/vilnius-date";
import { AppDispatch, RootState } from "configureStore";
import BillingStartField from "features/billing-period/billing-start-field";
import ContractTermField, {
  ContractTerm,
  endDateOf,
  isContractTermIncomplete,
  termFromEndDate,
} from "features/billing-period/contract-term-field";
import Loading from "components/loading/Loading";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import {
  fetchById,
  patchOrderInvoiceSettings,
} from "services/orders.service";
import {
  BillingPeriodMode,
  defaultBillingPeriodMode,
  defaultBillingStartMonth,
  fromMonthInput,
  isValidScheduleDay,
  toMonthInput,
} from "features/billing-period/billing-period";
import { Order } from "types/Order";
import { canManageSalesInvoiceSettings } from "utils/salesInvoicePermissions";
import { resolveOrderSaveErrorMessage } from "utils/orderInvoiceSettingsError";

type Props = {
  id: string;
};

/** Order-level sales invoice billing settings only (long-term / schedule / end date). */
export default function InvoiceSettingsForm({ id }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { companyId } = useParams<{ companyId: string }>();
  const { role } = useSelector((state: RootState) => state.authReducer);
  // Same tier the backend's `canManageSalesInvoices` policy requires on this
  // route (PATCH orders/:id/invoice-settings) — see
  // utils/salesInvoicePermissions.ts.
  const canManageInvoiceSettings = canManageSalesInvoiceSettings(role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [term, setTerm] = useState<ContractTerm>(termFromEndDate(""));
  const [termError, setTermError] = useState("");
  /** The end date as loaded — sent back only if the user changes it. */
  const loadedContractEndRef = useRef("");
  const [invoiceScheduleMode, setInvoiceScheduleMode] = useState(
    "last_day_of_month"
  );
  const [invoiceScheduleDay, setInvoiceScheduleDay] = useState("1");
  const [billingPeriodMode, setBillingPeriodMode] = useState<BillingPeriodMode>(
    "current_month"
  );
  /** YYYY-MM; empty = the server's default (see defaultBillingStartMonth). */
  const [billingStartMonth, setBillingStartMonth] = useState("");
  const [loadedStartMonth, setLoadedStartMonth] = useState("");
  const [order, setOrder] = useState<Pick<
    Order,
    "date" | "createdAt" | "billingCapPeriods"
  > | null>(null);

  const dayInvalid =
    isRecurring &&
    invoiceScheduleMode === "specific_day" &&
    !isValidScheduleDay(invoiceScheduleDay);
  const capMonth = toMonthInput(order?.billingCapPeriods?.[billingPeriodMode]);
  const billingStartDefault = defaultBillingStartMonth({
    capPeriod: order?.billingCapPeriods?.[billingPeriodMode],
    orderDate: order?.date,
    createdAt: order?.createdAt,
  });

  /** A new schedule brings its own default period mode, as on the server. */
  const handleScheduleModeChange = (value: string) => {
    setInvoiceScheduleMode(value);
    setBillingPeriodMode(defaultBillingPeriodMode(value));
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id || !companyId) return;
      setLoading(true);
      setErrMsg("");
      try {
        const { orderEntity } = await fetchById(id, companyId);
        if (cancelled) return;
        setIsRecurring(!!orderEntity.isRecurring);
        // The Vilnius day, not the browser's: read with date-fns in a zone west
        // of UTC, the stored midnight became the day before — and was then sent
        // back on every save, walking the contract end back a day each time.
        const loadedEnd = vilniusYmd(orderEntity.contractEndDate);
        setTerm(termFromEndDate(loadedEnd));
        loadedContractEndRef.current = loadedEnd;
        setInvoiceScheduleMode(
          orderEntity.invoiceScheduleMode === "specific_day"
            ? "specific_day"
            : "last_day_of_month"
        );
        setInvoiceScheduleDay(
          orderEntity.invoiceScheduleDay
            ? String(orderEntity.invoiceScheduleDay)
            : "1"
        );
        setBillingPeriodMode(
          orderEntity.billingPeriodModeEffective ||
            orderEntity.billingPeriodMode ||
            defaultBillingPeriodMode(orderEntity.invoiceScheduleMode)
        );
        // Only a stored start fills the field; the default shows as the
        // placeholder.
        const loadedStart = toMonthInput(orderEntity.billingStartDate);
        setBillingStartMonth(loadedStart);
        setLoadedStartMonth(loadedStart);
        setOrder({
          date: orderEntity.date,
          createdAt: orderEntity.createdAt,
          billingCapPeriods: orderEntity.billingCapPeriods,
        });
      } catch (e: any) {
        if (!cancelled) {
          setErrMsg(
            e?.response?.data?.message ||
              e?.message ||
              t("error", { defaultValue: "Error" })
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, companyId, t]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!companyId || !id) return;
    // The route is gated by `canManageSalesInvoices` for every request —
    // the fields are already disabled for this user, but a defensive check
    // here avoids a pointless round-trip to the server for a 403.
    if (!canManageInvoiceSettings) return;
    // The server refuses these too; catching them here keeps the modal open
    // with the field still in front of the user.
    if (dayInvalid) {
      setErrMsg(t("order_invoice_schedule_day_invalid"));
      return;
    }
    if (isRecurring && isContractTermIncomplete(term)) {
      setTermError(t("contract_term_date_required"));
      return;
    }
    setSaving(true);
    setErrMsg("");
    try {
      const contractEndDate = endDateOf(term);
      await patchOrderInvoiceSettings({
        companyId,
        orderId: id,
        data: {
          isRecurring,
          // Only when changed: an untouched end date is left exactly as the
          // server has it rather than round-tripped through the browser.
          ...(contractEndDate !== loadedContractEndRef.current
            ? { contractEndDate: isRecurring && contractEndDate ? contractEndDate : null }
            : {}),
          invoiceScheduleMode: isRecurring
            ? invoiceScheduleMode
            : "last_day_of_month",
          invoiceScheduleDay:
            isRecurring && invoiceScheduleMode === "specific_day"
              ? parseInt(invoiceScheduleDay, 10)
              : null,
          billingPeriodMode: isRecurring ? billingPeriodMode : null,
          billingStartDate: isRecurring ? fromMonthInput(billingStartMonth) : null,
        },
      });
      dispatch(fetchOrderDetails({ companyId, orderId: id }));
      toast.success(t("saved_successfully", { defaultValue: "Saved" }));
      closeModal();
    } catch (e: any) {
      // This route is gated by `canManageSalesInvoices` for every request
      // (it edits nothing else), so a bare 403 here already means the
      // permission hint — see utils/orderInvoiceSettingsError.ts.
      setErrMsg(resolveOrderSaveErrorMessage(e, t, { dedicatedEndpoint: true }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errMsg ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {errMsg}
        </div>
      ) : null}

      <p className="text-sm text-zinc-500">
        {t("invoice_settings_hint", {
          defaultValue:
            "Configure how sales invoices are generated for this order. Order products and document details are edited separately.",
        })}
      </p>

      {!canManageInvoiceSettings && (
        <p className="text-sm text-zinc-500">
          {t("invoice_settings_permission_error", {
            defaultValue:
              "Only users who manage sales invoices can change billing settings.",
          })}
        </p>
      )}

      <div className="flex items-start gap-3">
        <Switch
          checked={isRecurring}
          onChange={setIsRecurring}
          disabled={!canManageInvoiceSettings}
          className={`${
            isRecurring ? "bg-indigo-600" : "bg-zinc-200 dark:bg-zinc-700"
          } relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <span
            className={`${
              isRecurring ? "translate-x-5" : "translate-x-1"
            } inline-block size-4 rounded-full bg-white transition`}
          />
        </Switch>
        <div>
          <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
            {t("order_is_recurring", {
              defaultValue: "Long-term contract (monthly invoices)",
            })}
          </p>
          <p className="mt-0.5 text-xs text-zinc-500">
            {t("order_is_recurring_help", {
              defaultValue:
                "Generate one sales invoice each month until the order is closed or the contract end date is reached.",
            })}
          </p>
        </div>
      </div>

      {isRecurring && (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>
              {t("order_invoice_schedule_mode", {
                defaultValue: "Invoice generation day",
              })}
            </label>
            <select
              className={inputCls}
              value={invoiceScheduleMode}
              onChange={(e) => handleScheduleModeChange(e.target.value)}
              disabled={!canManageInvoiceSettings}
            >
              <option value="last_day_of_month">
                {t("order_invoice_schedule_last_day", {
                  defaultValue: "Last day of the month",
                })}
              </option>
              <option value="specific_day">
                {t("order_invoice_schedule_specific_day", {
                  defaultValue: "Specific day of the month",
                })}
              </option>
            </select>
          </div>
          {invoiceScheduleMode === "specific_day" && (
            <div>
              <label className={labelCls}>
                {t("order_invoice_schedule_day", {
                  defaultValue: "Day of month (1–28)",
                })}
              </label>
              <input
                className={inputCls}
                type="number"
                min={1}
                max={28}
                step={1}
                aria-invalid={dayInvalid}
                value={invoiceScheduleDay}
                onChange={(e) => setInvoiceScheduleDay(e.target.value)}
                disabled={!canManageInvoiceSettings}
              />
              {dayInvalid ? (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  {t("order_invoice_schedule_day_invalid")}
                </p>
              ) : null}
            </div>
          )}
          <div>
            <label className={labelCls}>{t("invoice_billing_period_mode")}</label>
            <select
              className={inputCls}
              value={billingPeriodMode}
              onChange={(e) =>
                setBillingPeriodMode(e.target.value as BillingPeriodMode)
              }
              disabled={!canManageInvoiceSettings}
            >
              <option value="current_month">
                {t("invoice_billing_period_current_month")}
              </option>
              <option value="previous_month">
                {t("invoice_billing_period_previous_month")}
              </option>
            </select>
            <p className="mt-1 text-xs text-zinc-500">
              {billingPeriodMode === "previous_month"
                ? t("invoice_billing_period_previous_month_help")
                : t("invoice_billing_period_current_month_help")}
            </p>
          </div>
          <BillingStartField
            value={billingStartMonth}
            onChange={setBillingStartMonth}
            loadedValue={loadedStartMonth}
            defaultMonth={billingStartDefault}
            capMonth={capMonth}
            readOnly={!canManageInvoiceSettings}
          />
          <ContractTermField
            value={term}
            onChange={(next) => {
              setTerm(next);
              setTermError("");
            }}
            error={termError}
            readOnly={!canManageInvoiceSettings}
          />
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button type="button" className={btnSecondaryCls} onClick={closeModal}>
          {t("cancel")}
        </button>
        <button
          type="submit"
          className={btnPrimaryCls}
          disabled={saving || !canManageInvoiceSettings}
        >
          {saving ? t("loading", { defaultValue: "Loading…" }) : t("save")}
        </button>
      </div>
    </form>
  );
}
